import type { OriginScope } from "@features/report/template/types/reportTemplate";
import type { FinanceDeductible } from "@features/admin/finance/deductible/types/financeDeductible";
import {
  buildFormulaPreview,
  isMeasuredQuantityDataTypeName,
  isNumericCompatibleDataTypeName,
  isStructuredDataTypeName,
  type KpiOperation,
} from "@features/admin/kpi/types/kpiEntryMode";
import {
  COLUMN_TYPES,
  DISTRIBUTION_ENGINES,
  INCLUDE_FLAG_BY_ORIGIN,
  ORIGIN_SCOPES,
  SECTION_TYPES,
  TOTALS_MODES,
  type ColumnType,
  type DistributionEngine,
  type LayoutCellInput,
  type LayoutColumnInput,
  type LayoutKpiMeta,
  type LayoutMetaKpi,
  type LayoutMetaTemplate,
  type LayoutRowInput,
  type LayoutSectionInput,
  type PresentationLayoutDetail,
  type PresentationLayoutIncludes,
  type PresentationLayoutMeta,
  type RenderedLayoutSection,
  type SectionType,
  type UpsertPresentationLayoutPayload,
} from "@features/presentationLayout/types/presentationLayout";
import {
  LABEL_USING_PREFIX,
  MAX_LABEL_LENGTH,
  MAX_OPERANDS_PER_COLUMN,
  MESSAGE_LABEL_REQUIRED,
  MESSAGE_LABEL_TOO_LONG,
  MESSAGE_ROW_TOTALS_NOT_NUMERIC,
} from "@features/presentationLayout/constants/presentationLayoutConstants";

export type DraftValidationResult = {
  messages: string[];
  fieldErrors: Record<string, string>;
};

export function labelFieldKey(sectionIndex: number, kind: "rows" | "columns", index: number): string {
  return `sections.${sectionIndex}.${kind}.${index}.label`;
}

export function usingLabelText(name: string): string {
  return `${LABEL_USING_PREFIX} ${name}`;
}

/** Client-only `key` keeps React list identity stable while rows/columns are added, moved and removed. */
export type DraftRow = Omit<LayoutRowInput, "cells"> & {
  key: string;
  cells: Array<LayoutCellInput | null>;
};
export type DraftColumn = LayoutColumnInput & { key: string };
export type DraftSection = Omit<LayoutSectionInput, "rows" | "columns"> & {
  key: string;
  rows: DraftRow[];
  columns: DraftColumn[];
};
export type LayoutDraft = PresentationLayoutIncludes & {
  name: string;
  description: string;
  origin_scope: OriginScope;
  sections: DraftSection[];
};

let keySeq = 0;
export const newDraftKey = (): string => `k${++keySeq}`;

export function emptyIncludes(origin: OriginScope): PresentationLayoutIncludes {
  return {
    include_church_templates: origin === "CHURCH",
    include_district_templates: false,
    include_nation_templates: false,
  };
}

export function emptyDraft(origin: OriginScope): LayoutDraft {
  return {
    name: "",
    description: "",
    origin_scope: origin,
    ...emptyIncludes(origin),
    sections: [],
  };
}

/** Default Bind cell: pickers empty until the author chooses template / activity / KPI. */
export function emptyCell(): LayoutCellInput {
  return { is_empty: false, report_template_id: null, activity_id: null, kpi_id: null };
}

/** Aligns row cells 1:1 with columns; Calculated slots stay null; new Value slots get Bind + empty pickers. */
export function syncRowCellsToColumns(row: DraftRow, columns: DraftColumn[]): DraftRow {
  const cells = columns.map((column, index) => {
    if (column.column_type === COLUMN_TYPES.CALCULATED) return null;
    if (column.column_type !== COLUMN_TYPES.VALUE) return row.cells[index] ?? null;
    return row.cells[index] ?? emptyCell();
  });
  return { ...row, cells };
}

export function withColumns(section: DraftSection, columns: DraftColumn[]): DraftSection {
  if (section.section_type !== SECTION_TYPES.BINDING_TABLE) {
    return { ...section, columns };
  }
  return {
    ...section,
    columns,
    rows: section.rows.map((row) => syncRowCellsToColumns(row, columns)),
  };
}

export function newSection(type: SectionType): DraftSection {
  const columns = type === SECTION_TYPES.BINDING_TABLE ? [newColumn(COLUMN_TYPES.VALUE)] : [];
  return {
    key: newDraftKey(),
    title: "",
    section_type: type,
    row_header_label: null,
    totals_mode: TOTALS_MODES.NONE,
    totals_label: null,
    rows: [],
    columns,
  };
}

export function newRow(columns: DraftColumn[] = []): DraftRow {
  return syncRowCellsToColumns(
    {
      key: newDraftKey(),
      label: "",
      report_template_id: null,
      activity_id: null,
      kpi_id: null,
      cells: [],
    },
    columns
  );
}

export function newColumn(type: ColumnType): DraftColumn {
  return { key: newDraftKey(), label: "", column_type: type, kpi_id: null, deductible_id: null, calc_operation: null, operands: [] };
}

export function draftFromDetail(detail: PresentationLayoutDetail): LayoutDraft {
  return {
    name: detail.name,
    description: detail.description ?? "",
    origin_scope: detail.origin_scope,
    include_church_templates: detail.include_church_templates,
    include_district_templates: detail.include_district_templates,
    include_nation_templates: detail.include_nation_templates,
    sections: detail.sections.map((section) => {
      const columns = section.columns.map((column) => ({
        key: newDraftKey(),
        label: column.label,
        column_type: column.column_type,
        kpi_id: column.kpi_id,
        deductible_id: column.deductible_id,
        calc_operation: column.calc_operation,
        operands: [...column.operands],
      }));
      const rows = section.rows.map((row) => {
        const draftRow: DraftRow = {
          key: newDraftKey(),
          label: row.label,
          report_template_id: row.report_template_id,
          activity_id: row.activity_id,
          kpi_id: row.kpi_id,
          cells: (row.cells ?? []).map((cell) => {
            if (cell === null) return null;
            return {
              is_empty: cell.is_empty,
              report_template_id: cell.report_template_id,
              activity_id: cell.activity_id,
              kpi_id: cell.kpi_id,
            };
          }),
        };
        return section.section_type === SECTION_TYPES.BINDING_TABLE
          ? syncRowCellsToColumns(draftRow, columns)
          : draftRow;
      });
      return {
        key: newDraftKey(),
        title: section.title,
        section_type: section.section_type,
        row_header_label: section.row_header_label,
        totals_mode: section.totals_mode,
        totals_label: section.totals_label,
        rows,
        columns,
      };
    }),
  };
}

const blankToNull = (value: string | null): string | null => {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Product default for an empty label at save time (KPI / activity / deductible name).
 * Returns null when a typed label is required or the binding is incomplete.
 */
export function defaultForSaveRow(
  sectionType: SectionType,
  row: DraftRow,
  catalog: BindingCatalog
): string | null {
  if (sectionType === SECTION_TYPES.BINDING_TABLE) return null;
  if (row.report_template_id === null || row.activity_id === null) return null;
  if (sectionType === SECTION_TYPES.KPI_MATRIX) {
    const name = catalog.activity(row.report_template_id, row.activity_id)?.activity_name?.trim() ?? "";
    return name !== "" ? name : null;
  }
  const name = catalog.kpi(row.report_template_id, row.activity_id, row.kpi_id)?.kpi_name?.trim() ?? "";
  return name !== "" ? name : null;
}

export function defaultForSaveColumn(
  sectionType: SectionType,
  column: DraftColumn,
  section: DraftSection,
  catalog: BindingCatalog,
  deductibleNames: Map<number, string>
): string | null {
  if (sectionType === SECTION_TYPES.BINDING_TABLE) return null;
  if (sectionType === SECTION_TYPES.DISTRIBUTION_TABLE && column.column_type === COLUMN_TYPES.DEDUCTIBLE) {
    if (column.deductible_id === null) return null;
    const name = deductibleNames.get(column.deductible_id)?.trim() ?? "";
    return name !== "" ? name : null;
  }
  if (sectionType === SECTION_TYPES.KPI_MATRIX && column.column_type === COLUMN_TYPES.KPI_BINDING) {
    const name = matrixColumnKpis(section, catalog).find((k) => k.kpi_id === column.kpi_id)?.kpi_name?.trim() ?? "";
    return name !== "" ? name : null;
  }
  return null;
}

function resolveSaveLabel(trimmed: string, defaultName: string | null): string {
  if (trimmed !== "") return trimmed;
  return defaultName ?? "";
}

export function toPayload(
  draft: LayoutDraft,
  meta: PresentationLayoutMeta,
  deductibles: FinanceDeductible[]
): UpsertPresentationLayoutPayload {
  const catalog = bindingCatalog(meta);
  const deductibleNames = new Map(deductibles.map((d) => [d.deductible_id, d.name]));

  return {
    name: draft.name.trim(),
    description: blankToNull(draft.description),
    origin_scope: draft.origin_scope,
    include_church_templates: draft.include_church_templates,
    include_district_templates: draft.include_district_templates,
    include_nation_templates: draft.include_nation_templates,
    sections: draft.sections.map(({ key: _key, rows, columns, ...section }) => {
      const isBinding = section.section_type === SECTION_TYPES.BINDING_TABLE;
      const draftSection: DraftSection = { ...section, key: _key, rows, columns };
      return {
        ...section,
        title: section.title.trim(),
        row_header_label: blankToNull(section.row_header_label),
        totals_label: blankToNull(section.totals_label),
        rows: rows.map((row) => {
          const synced = isBinding ? syncRowCellsToColumns(row, columns) : row;
          const label = resolveSaveLabel(
            synced.label.trim(),
            defaultForSaveRow(section.section_type, synced, catalog)
          );
          if (isBinding) {
            return {
              label,
              report_template_id: null,
              activity_id: null,
              kpi_id: null,
              cells: synced.cells.map((cell) => {
                if (cell === null) return null;
                if (cell.is_empty) {
                  return { is_empty: true, report_template_id: null, activity_id: null, kpi_id: null };
                }
                return {
                  is_empty: false,
                  report_template_id: cell.report_template_id,
                  activity_id: cell.activity_id,
                  kpi_id: cell.kpi_id,
                };
              }),
            };
          }
          return {
            label,
            report_template_id: synced.report_template_id,
            activity_id: synced.activity_id,
            kpi_id: section.section_type === SECTION_TYPES.KPI_MATRIX ? null : synced.kpi_id,
          };
        }),
        columns: section.section_type === SECTION_TYPES.FIELD_LIST
          ? []
          : columns.map(({ key: _columnKey, ...column }) => ({
              ...column,
              label: resolveSaveLabel(
                column.label.trim(),
                defaultForSaveColumn(section.section_type, column as DraftColumn, draftSection, catalog, deductibleNames)
              ),
              kpi_id: column.column_type === COLUMN_TYPES.KPI_BINDING ? column.kpi_id : null,
              deductible_id: column.column_type === COLUMN_TYPES.DEDUCTIBLE ? column.deductible_id : null,
              calc_operation: column.column_type === COLUMN_TYPES.CALCULATED ? column.calc_operation : null,
              operands: column.column_type === COLUMN_TYPES.CALCULATED ? column.operands : [],
            })),
      };
    }),
  };
}

/** Adds or removes a calculated column's operand, kept in column order; adding stops at MAX_OPERANDS_PER_COLUMN. */
export function toggleOperand(operands: number[], operand: number): number[] {
  if (operands.includes(operand)) return operands.filter((o) => o !== operand);
  if (operands.length >= MAX_OPERANDS_PER_COLUMN) return operands;
  return [...operands, operand].sort((a, b) => a - b);
}

/** Removes a column; calculated columns drop it from their operands and shift later indexes down. */
export function removeColumnAt(columns: DraftColumn[], index: number): DraftColumn[] {
  return columns
    .filter((_, i) => i !== index)
    .map((column) => ({
      ...column,
      operands: column.operands.filter((o) => o !== index).map((o) => (o > index ? o - 1 : o)),
    }));
}

/** Removes a column and drops the matching cell slot on every Binding table row. */
export function removeColumnFromSection(section: DraftSection, index: number): DraftSection {
  const columns = removeColumnAt(section.columns, index);
  const rows = section.rows.map((row) => ({
    ...row,
    cells: row.cells.filter((_, i) => i !== index),
  }));
  return withColumns({ ...section, rows }, columns);
}

/** Swaps two adjacent columns and remaps operands so every calculation keeps pointing at the same column. */
export function swapColumns(columns: DraftColumn[], a: number, b: number): DraftColumn[] {
  if (a < 0 || b < 0 || a >= columns.length || b >= columns.length) return columns;
  const remap = (o: number) => (o === a ? b : o === b ? a : o);
  const next = columns.map((column) => ({ ...column, operands: column.operands.map(remap) }));
  [next[a], next[b]] = [next[b], next[a]];
  return next;
}

/** Swaps two adjacent columns and swaps Matching Binding table cell slots. */
export function swapColumnsInSection(section: DraftSection, a: number, b: number): DraftSection {
  const columns = swapColumns(section.columns, a, b);
  if (section.section_type !== SECTION_TYPES.BINDING_TABLE) {
    return { ...section, columns };
  }
  const rows = section.rows.map((row) => {
    const cells = [...row.cells];
    while (cells.length < section.columns.length) cells.push(null);
    [cells[a], cells[b]] = [cells[b], cells[a]];
    return { ...row, cells };
  });
  return withColumns({ ...section, rows }, columns);
}

export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/** Template origins currently ticked, limited to what the layout origin may include. */
export function includedOrigins(draft: LayoutDraft, meta: PresentationLayoutMeta): OriginScope[] {
  const allowed = meta.allowed_includes[draft.origin_scope] ?? [];
  return ORIGIN_SCOPES.filter((origin) => allowed.includes(origin) && draft[INCLUDE_FLAG_BY_ORIGIN[origin]]);
}

export function templatesForDraft(draft: LayoutDraft, meta: PresentationLayoutMeta): LayoutMetaTemplate[] {
  const origins = includedOrigins(draft, meta);
  return meta.templates.filter((template) => origins.includes(template.origin_scope));
}

export type BindingCatalog = {
  template: (templateId: number) => LayoutMetaTemplate | undefined;
  activity: (templateId: number, activityId: number) => LayoutMetaTemplate["activities"][number] | undefined;
  kpi: (templateId: number, activityId: number, kpiId: number | null) => LayoutMetaKpi | undefined;
};

export function bindingCatalog(meta: PresentationLayoutMeta): BindingCatalog {
  const templates = new Map(meta.templates.map((t) => [t.report_template_id, t]));
  const activity = (templateId: number, activityId: number) =>
    templates.get(templateId)?.activities.find((a) => a.activity_id === activityId);

  return {
    template: (templateId) => templates.get(templateId),
    activity,
    kpi: (templateId, activityId, kpiId) =>
      kpiId === null ? undefined : activity(templateId, activityId)?.kpis.find((k) => k.kpi_id === kpiId),
  };
}

/** KPIs available as matrix columns: the union of KPIs on the section's row activities. */
export function matrixColumnKpis(section: DraftSection, catalog: BindingCatalog): LayoutMetaKpi[] {
  const byId = new Map<number, LayoutMetaKpi>();
  section.rows.forEach((row) => {
    if (row.report_template_id === null || row.activity_id === null) return;
    catalog.activity(row.report_template_id, row.activity_id)?.kpis.forEach((kpi) => {
      if (!byId.has(kpi.kpi_id)) byId.set(kpi.kpi_id, kpi);
    });
  });
  return Array.from(byId.values());
}

/** Short editor summary for a Binding table row (column label → KPI name or — / incomplete). */
export function bindingRowSummary(
  row: DraftRow,
  columns: DraftColumn[],
  catalog: BindingCatalog
): string {
  const synced = syncRowCellsToColumns(row, columns);
  const parts = columns
    .map((column, index) => {
      if (column.column_type !== COLUMN_TYPES.VALUE) return null;
      const label = column.label.trim() || `Column ${index + 1}`;
      const cell = synced.cells[index];
      if (!cell || cell.is_empty) return `${label}: —`;
      if (cell.report_template_id === null || cell.activity_id === null) return `${label}: (incomplete)`;
      const kpi = catalog.kpi(cell.report_template_id, cell.activity_id, cell.kpi_id);
      if (!kpi) return `${label}: (incomplete)`;
      return `${label}: ${kpi.kpi_name}`;
    })
    .filter((part): part is string => part !== null);
  return parts.join(" · ");
}

/** KPIs a field list row or matrix column can show; single-choice, multiple-choice and grid KPIs are left out. */
export function displayableKpis(kpis: LayoutMetaKpi[]): LayoutMetaKpi[] {
  return kpis.filter((kpi) => !isStructuredDataTypeName(kpi.data_type));
}

/**
 * Picker options for a row or column KPI: the displayable KPIs, plus the bound KPI as a disabled option when it is
 * a structured KPI saved before they were left out, so the picker shows it instead of "Select…".
 */
export function kpiPickerOptions(
  kpis: LayoutMetaKpi[],
  selectedKpiId: number | null
): { kpi: LayoutMetaKpi; disabled: boolean }[] {
  const options = displayableKpis(kpis).map((kpi) => ({ kpi, disabled: false }));
  const selected = selectedKpiId !== null ? kpis.find((kpi) => kpi.kpi_id === selectedKpiId) : undefined;
  if (selected && isStructuredDataTypeName(selected.data_type)) {
    options.push({ kpi: selected, disabled: true });
  }
  return options;
}

export function engineForKpi(kpiId: number | null, tithesKpiId: number): DistributionEngine {
  return kpiId !== null && kpiId === tithesKpiId ? DISTRIBUTION_ENGINES.CATEGORY : DISTRIBUTION_ENGINES.ALLOCATION_RULE;
}

export function requiresExactlyTwo(meta: PresentationLayoutMeta, operation: KpiOperation | null): boolean {
  return meta.calc_operations.find((op) => op.operation === operation)?.requires_exactly_two ?? false;
}

/** Human-readable formula for a calculated column, e.g. "Tithes − Pool". */
export function formulaHint(
  column: Pick<LayoutColumnInput, "calc_operation" | "operands">,
  columns: Array<{ label: string }>
): string {
  return buildFormulaPreview(
    column.calc_operation,
    column.operands.map((index) => ({
      source_kpi_id: index,
      kpi_name: columns[index]?.label?.trim() || `Column ${index + 1}`,
    }))
  );
}

function columnIsNumeric(column: DraftColumn, kpi: LayoutMetaKpi | undefined): boolean {
  if (column.column_type === COLUMN_TYPES.KPI_BINDING) {
    return isNumericCompatibleDataTypeName(kpi?.data_type);
  }
  // Value / Source / Deductible / Calculated: treat as numeric for operand checks.
  return true;
}

/**
 * Mirrors PresentationLayoutValidator (ntcog-api) so authors see problems before saving.
 * The API remains the authority; this only catches what can be checked from meta and the deductible list.
 */
export function validateDraft(
  draft: LayoutDraft,
  meta: PresentationLayoutMeta,
  deductibles: FinanceDeductible[]
): DraftValidationResult {
  const errors: string[] = [];
  const fieldErrors: Record<string, string> = {};
  const catalog = bindingCatalog(meta);
  const deductibleNames = new Map(deductibles.map((d) => [d.deductible_id, d.name]));
  const origins = includedOrigins(draft, meta);
  const activeDeductibles = new Set(deductibles.filter((d) => d.is_active).map((d) => d.deductible_id));

  const requireLabel = (
    sectionIndex: number,
    kind: "rows" | "columns",
    index: number,
    sectionTitle: string,
    trimmed: string,
    defaultName: string | null
  ) => {
    if (trimmed !== "") {
      if (trimmed.length > MAX_LABEL_LENGTH) {
        const message = `${sectionTitle} — ${kind === "rows" ? "row" : "column"} ${index + 1}: ${MESSAGE_LABEL_TOO_LONG}`;
        errors.push(message);
        fieldErrors[labelFieldKey(sectionIndex, kind, index)] = MESSAGE_LABEL_TOO_LONG;
      }
      return;
    }
    if (defaultName !== null) {
      if (defaultName.length > MAX_LABEL_LENGTH) {
        const message = `${sectionTitle} — ${kind === "rows" ? "row" : "column"} ${index + 1}: ${MESSAGE_LABEL_TOO_LONG}`;
        errors.push(message);
        fieldErrors[labelFieldKey(sectionIndex, kind, index)] = MESSAGE_LABEL_TOO_LONG;
      }
      return;
    }
    const message = `${sectionTitle} — ${kind === "rows" ? "row" : "column"} ${index + 1}: ${MESSAGE_LABEL_REQUIRED}`;
    errors.push(message);
    fieldErrors[labelFieldKey(sectionIndex, kind, index)] = MESSAGE_LABEL_REQUIRED;
  };

  if (!draft.name.trim()) errors.push("Name is required.");
  if (origins.length === 0) errors.push("Choose at least one set of templates to include.");
  if (draft.sections.length === 0) errors.push("Add at least one section.");
  if (draft.sections.length > meta.limits.sections) {
    errors.push(`A layout can have at most ${meta.limits.sections} sections.`);
  }

  draft.sections.forEach((section, sectionIndex) => {
    const name = section.title.trim() || `Section ${sectionIndex + 1}`;
    const type = section.section_type;
    const columns = type === SECTION_TYPES.FIELD_LIST ? [] : section.columns;

    if (!section.title.trim()) errors.push(`${name}: title is required.`);
    if (!(meta.section_totals_modes[type] ?? []).includes(section.totals_mode)) {
      errors.push(`${name}: this section type does not support ${section.totals_mode.toLowerCase()} totals.`);
    }
    if (section.rows.length === 0) errors.push(`${name}: add at least one row.`);
    if (section.rows.length > meta.limits.rows_per_section) {
      errors.push(`${name}: at most ${meta.limits.rows_per_section} rows.`);
    }
    if (type !== SECTION_TYPES.FIELD_LIST && columns.length === 0) errors.push(`${name}: add at least one column.`);
    if (columns.length > meta.limits.columns_per_section) {
      errors.push(`${name}: at most ${meta.limits.columns_per_section} columns.`);
    }

    const isBinding = type === SECTION_TYPES.BINDING_TABLE;
    if (isBinding && !columns.some((column) => column.column_type === COLUMN_TYPES.VALUE)) {
      errors.push(`${name}: needs at least one Value column.`);
    }

    let allChurchRows = true;
    section.rows.forEach((row, rowIndex) => {
      const rowLabel = row.label.trim() || String(rowIndex + 1);
      const rowName = `${name}, row ${rowLabel}`;
      requireLabel(
        sectionIndex,
        "rows",
        rowIndex,
        name,
        row.label.trim(),
        defaultForSaveRow(type, row, catalog)
      );

      if (isBinding) {
        const synced = syncRowCellsToColumns(row, columns);
        let hasBound = false;
        synced.cells.forEach((cell, columnIndex) => {
          const column = columns[columnIndex];
          const columnLabel = column?.label.trim() || `Column ${columnIndex + 1}`;
          if (column?.column_type === COLUMN_TYPES.CALCULATED) {
            if (cell !== null) {
              errors.push(`${rowName}: column ${columnLabel} is Calculated and must not have a cell binding.`);
            }
            return;
          }
          if (column?.column_type !== COLUMN_TYPES.VALUE) return;
          if (cell === null) {
            errors.push(`${rowName}: needs a cell for every column.`);
            return;
          }
          if (cell.is_empty) return;
          hasBound = true;
          if (
            cell.report_template_id === null
            || cell.activity_id === null
            || cell.kpi_id === null
            || !catalog.kpi(cell.report_template_id, cell.activity_id, cell.kpi_id)
          ) {
            errors.push(
              `Row "${rowLabel}": column "${columnLabel}" needs a template, activity, and KPI — or choose Leave blank.`
            );
            return;
          }
          const template = catalog.template(cell.report_template_id);
          if (!template) {
            errors.push(`${rowName}: column ${columnLabel} uses a template that is not available.`);
            return;
          }
          if (!origins.includes(template.origin_scope)) {
            errors.push(
              `${rowName}: column ${columnLabel} uses ${template.report_template_name}, which is not in the included templates.`
            );
            return;
          }
          const kpi = catalog.kpi(cell.report_template_id, cell.activity_id, cell.kpi_id);
          if (kpi && isStructuredDataTypeName(kpi.data_type)) {
            errors.push(`${rowName}: column ${columnLabel} uses a KPI type that layouts cannot show.`);
          }
        });
        if (!hasBound) {
          errors.push(`${name}: row ${rowLabel} needs at least one bound Value cell.`);
        }
        return;
      }

      if (row.report_template_id === null || row.activity_id === null) {
        errors.push(`${rowName}: choose a template.`);
        allChurchRows = false;
        return;
      }
      const template = catalog.template(row.report_template_id);
      if (!template) {
        errors.push(`${rowName}: choose a template.`);
        allChurchRows = false;
        return;
      }
      allChurchRows = allChurchRows && template.origin_scope === "CHURCH";
      if (!origins.includes(template.origin_scope)) {
        errors.push(`${rowName}: ${template.report_template_name} is not in the included templates.`);
      }
      if (!catalog.activity(row.report_template_id, row.activity_id)) {
        errors.push(`${rowName}: choose an activity.`);
        return;
      }
      if (type === SECTION_TYPES.KPI_MATRIX) return;

      const kpi = catalog.kpi(row.report_template_id, row.activity_id, row.kpi_id);
      if (!kpi) {
        errors.push(`${rowName}: choose a KPI.`);
      } else if (type === SECTION_TYPES.DISTRIBUTION_TABLE && !isMeasuredQuantityDataTypeName(kpi.data_type)) {
        errors.push(`${rowName}: distribution rows need a numeric or currency KPI.`);
      } else if (isStructuredDataTypeName(kpi.data_type)) {
        errors.push(`${rowName}: uses a KPI type that layouts cannot show.`);
      }
    });

    const matrixKpis = type === SECTION_TYPES.KPI_MATRIX ? matrixColumnKpis(section, catalog) : [];
    const kpiDataTypes = new Set<string>();
    const numeric: boolean[] = [];
    const valueColumnTypes: Array<Set<string>> = columns.map(() => new Set());

    columns.forEach((column, columnIndex) => {
      const columnName = `${name}, column ${column.label.trim() || columnIndex + 1}`;
      requireLabel(
        sectionIndex,
        "columns",
        columnIndex,
        name,
        column.label.trim(),
        defaultForSaveColumn(type, column, section, catalog, deductibleNames)
      );
      if (!(meta.section_column_types[type] ?? []).includes(column.column_type)) {
        errors.push(`${columnName}: column type not allowed in this section.`);
      }

      const kpi = column.column_type === COLUMN_TYPES.KPI_BINDING
        ? matrixKpis.find((k) => k.kpi_id === column.kpi_id)
        : undefined;

      if (column.column_type === COLUMN_TYPES.KPI_BINDING) {
        if (!kpi) errors.push(`${columnName}: choose a KPI found on the section's activities.`);
        else if (isStructuredDataTypeName(kpi.data_type)) errors.push(`${columnName}: uses a KPI type that layouts cannot show.`);
        else kpiDataTypes.add(kpi.data_type ?? "");
      }

      if (column.column_type === COLUMN_TYPES.DEDUCTIBLE) {
        if (column.deductible_id === null) errors.push(`${columnName}: choose a deductible.`);
        else if (!activeDeductibles.has(column.deductible_id)) errors.push(`${columnName}: the deductible is inactive or missing.`);
        if (!allChurchRows) errors.push(`${name}: deductible columns need every row to come from a church template.`);
      }

      if (column.column_type === COLUMN_TYPES.CALCULATED) {
        const operation = column.calc_operation;
        const operands = column.operands;
        if (!operation) errors.push(`${columnName}: choose an operation.`);
        else if (requiresExactlyTwo(meta, operation) && operands.length !== 2) {
          errors.push(`${columnName}: ${operation.toLowerCase()} needs exactly 2 columns.`);
        } else if (!requiresExactlyTwo(meta, operation) && operands.length < 2) {
          errors.push(`${columnName}: choose at least 2 columns.`);
        }
        if (operands.length > MAX_OPERANDS_PER_COLUMN) {
          errors.push(`${columnName}: at most ${MAX_OPERANDS_PER_COLUMN} columns.`);
        }
        if (new Set(operands).size !== operands.length) errors.push(`${columnName}: a column is used twice.`);
        if (operands.some((index) => index < 0 || index >= columnIndex)) {
          errors.push(`${columnName}: calculations can only use columns to their left.`);
        } else if (operands.some((index) => !numeric[index])) {
          errors.push(`${columnName}: calculations can only use numeric columns.`);
        }
      }

      numeric[columnIndex] = columnIsNumeric(column, kpi);
    });

    if (isBinding && (section.totals_mode === TOTALS_MODES.COLUMN || section.totals_mode === TOTALS_MODES.BOTH)) {
      section.rows.forEach((row) => {
        const synced = syncRowCellsToColumns(row, columns);
        synced.cells.forEach((cell, columnIndex) => {
          if (!cell || cell.is_empty || cell.report_template_id === null || cell.activity_id === null) return;
          const cellKpi = catalog.kpi(cell.report_template_id, cell.activity_id, cell.kpi_id);
          if (cellKpi?.data_type) valueColumnTypes[columnIndex].add(cellKpi.data_type);
        });
      });
      columns.forEach((column, columnIndex) => {
        if (column.column_type !== COLUMN_TYPES.VALUE) return;
        const types = valueColumnTypes[columnIndex];
        const columnLabel = column.label.trim() || `Column ${columnIndex + 1}`;
        if (types.size > 1) {
          errors.push(`${name}: column totals need every bound KPI in column ${columnLabel} to share one data type.`);
        } else if (types.size === 1 && !isNumericCompatibleDataTypeName([...types][0])) {
          errors.push(
            `${name}: column totals need number, currency, percentage or Yes/No values in column ${columnLabel}.`
          );
        }
      });
    }

    const rowTotals = type === SECTION_TYPES.KPI_MATRIX
      && (section.totals_mode === TOTALS_MODES.ROW || section.totals_mode === TOTALS_MODES.BOTH);
    if (rowTotals && kpiDataTypes.size > 1) {
      errors.push(`${name}: row totals need every KPI column to have the same data type.`);
    } else if (rowTotals && kpiDataTypes.size === 1 && !isNumericCompatibleDataTypeName([...kpiDataTypes][0])) {
      errors.push(`${name}: ${MESSAGE_ROW_TOTALS_NOT_NUMERIC}`);
    }
  });

  return { messages: Array.from(new Set(errors)), fieldErrors };
}

/** Structure-only render of the draft in the report form's `data.layout.sections` shape, with empty values. */
export function previewSections(
  draft: LayoutDraft,
  meta: PresentationLayoutMeta,
  deductibles: FinanceDeductible[]
): RenderedLayoutSection[] {
  const catalog = bindingCatalog(meta);
  const deductibleNames = new Map(deductibles.map((d) => [d.deductible_id, d.name]));

  return draft.sections.map((section, sectionIndex) => {
    const header = {
      section_id: sectionIndex + 1,
      title: section.title.trim() || `Section ${sectionIndex + 1}`,
      row_header_label: blankToNull(section.row_header_label),
      totals_mode: section.totals_mode,
      totals_label: blankToNull(section.totals_label),
    };
    const kpiOf = (row: DraftRow): LayoutKpiMeta | null => {
      if (row.report_template_id === null || row.activity_id === null) return null;
      return catalog.kpi(row.report_template_id, row.activity_id, row.kpi_id) ?? null;
    };
    const rowLabel = (row: DraftRow, index: number): string => {
      if (section.section_type === SECTION_TYPES.BINDING_TABLE) {
        return row.label.trim() || `Row ${index + 1}`;
      }
      return row.label.trim()
        || kpiOf(row)?.kpi_name
        || (row.report_template_id !== null && row.activity_id !== null
          ? catalog.activity(row.report_template_id, row.activity_id)?.activity_name
          : undefined)
        || `Row ${index + 1}`;
    };

    if (section.section_type === SECTION_TYPES.FIELD_LIST) {
      return {
        ...header,
        section_type: SECTION_TYPES.FIELD_LIST,
        rows: section.rows.map((row, index) => ({ row_id: index + 1, label: rowLabel(row, index), kpi: kpiOf(row), value: null })),
      };
    }

    const matrixKpis = section.section_type === SECTION_TYPES.KPI_MATRIX ? matrixColumnKpis(section, catalog) : [];
    const withColumnTotals = section.totals_mode === TOTALS_MODES.COLUMN || section.totals_mode === TOTALS_MODES.BOTH;
    const withRowTotals = section.section_type === SECTION_TYPES.KPI_MATRIX
      && (section.totals_mode === TOTALS_MODES.ROW || section.totals_mode === TOTALS_MODES.BOTH);
    const isBinding = section.section_type === SECTION_TYPES.BINDING_TABLE;

    return {
      ...header,
      section_type: section.section_type,
      columns: section.columns.map((column, index) => {
        const kpi = column.column_type === COLUMN_TYPES.KPI_BINDING
          ? matrixKpis.find((k) => k.kpi_id === column.kpi_id) ?? null
          : null;
        const deductibleName = column.deductible_id !== null ? deductibleNames.get(column.deductible_id) ?? null : null;
        return {
          column_id: index + 1,
          label: column.label.trim() || kpi?.kpi_name || deductibleName || `Column ${index + 1}`,
          column_type: column.column_type,
          kpi,
          deductible_id: column.deductible_id,
          deductible_name: deductibleName,
          calc_operation: column.calc_operation,
          operands: column.operands,
        };
      }),
      rows: section.rows.map((row, index) => {
        const synced = isBinding ? syncRowCellsToColumns(row, section.columns) : row;
        return {
          row_id: index + 1,
          label: rowLabel(row, index),
          cells: section.columns.map(() => null),
          ...(withRowTotals ? { row_total: null } : {}),
          ...(section.section_type === SECTION_TYPES.DISTRIBUTION_TABLE
            ? { kpi: kpiOf(row), distribution_engine: engineForKpi(row.kpi_id, meta.tithes_kpi_id) }
            : {}),
          ...(isBinding
            ? {
              cell_kpis: synced.cells.map((cell) => {
                if (!cell || cell.is_empty || cell.report_template_id === null || cell.activity_id === null) {
                  return null;
                }
                return catalog.kpi(cell.report_template_id, cell.activity_id, cell.kpi_id) ?? null;
              }),
            }
            : {}),
        };
      }),
      column_totals: withColumnTotals ? section.columns.map(() => null) : null,
      grand_total: null,
    };
  });
}

/** Detail sections (`GET /{id}`) in the rendered shape with empty values, for the list preview eye. */
export function previewSectionsFromDetail(detail: PresentationLayoutDetail): RenderedLayoutSection[] {
  return detail.sections.map((section) => {
    const header = {
      section_id: section.section_id,
      title: section.title,
      row_header_label: section.row_header_label,
      totals_mode: section.totals_mode,
      totals_label: section.totals_label,
    };
    if (section.section_type === SECTION_TYPES.FIELD_LIST) {
      return {
        ...header,
        section_type: SECTION_TYPES.FIELD_LIST,
        rows: section.rows.map((row) => ({ row_id: row.row_id, label: row.label, kpi: row.kpi, value: null })),
      };
    }

    const withColumnTotals = section.totals_mode === TOTALS_MODES.COLUMN || section.totals_mode === TOTALS_MODES.BOTH;
    const withRowTotals = section.section_type === SECTION_TYPES.KPI_MATRIX
      && (section.totals_mode === TOTALS_MODES.ROW || section.totals_mode === TOTALS_MODES.BOTH);
    const isBinding = section.section_type === SECTION_TYPES.BINDING_TABLE;

    return {
      ...header,
      section_type: section.section_type,
      columns: section.columns.map((column) => ({
        column_id: column.column_id,
        label: column.label,
        column_type: column.column_type,
        kpi: column.kpi,
        deductible_id: column.deductible_id,
        deductible_name: column.deductible_name,
        calc_operation: column.calc_operation,
        operands: column.operands,
      })),
      rows: section.rows.map((row) => ({
        row_id: row.row_id,
        label: row.label,
        cells: section.columns.map(() => null),
        ...(withRowTotals ? { row_total: null } : {}),
        ...(section.section_type === SECTION_TYPES.DISTRIBUTION_TABLE
          ? { kpi: row.kpi, distribution_engine: row.distribution_engine }
          : {}),
        ...(isBinding
          ? {
            cell_kpis: (row.cells ?? []).map((cell) => (cell === null ? null : cell.kpi)),
          }
          : {}),
      })),
      column_totals: withColumnTotals ? section.columns.map(() => null) : null,
      grand_total: null,
    };
  });
}

const EMPTY_VALUE = "—";
const DATA_TYPE_BOOLEAN = "BOOLEAN";

/** Formats a rendered value with the KPI's decimal places and unit; text values pass through. */
export function formatLayoutValue(value: number | string | null | undefined, kpi?: LayoutKpiMeta | null): string {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;
  if ((kpi?.data_type ?? "").toUpperCase() === DATA_TYPE_BOOLEAN) {
    return typeof value === "string" ? value : EMPTY_VALUE;
  }
  if (typeof value === "string") return value;

  const decimals = kpi?.decimal_places ?? (Number.isInteger(value) ? 0 : 2);
  const formatted = value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const unit = kpi?.unit_abbreviation || kpi?.unit_name;
  if (!unit) return formatted;

  return (kpi?.unit_position ?? "").toLowerCase() === "prefix" ? `${unit}${formatted}` : `${formatted} ${unit}`;
}
